// Regras de leitura da fila de KYC: status em pt-BR, idade da submissão (o destaque da
// tela, item 1 da task), rótulo de documento e os dois erros que viram estado próprio.
// Decisão sempre pelo `error.code`/`details`, nunca pela `message` (`spec/17` §4.3).

import { isApiError } from '../../api/errors.ts';
import type {
  AdminKycFileKind,
  AdminKycStatus,
  AdminKycView,
} from '../../api/queries/admin-kyc.ts';
import type { PillTone } from '../../components/kit/pill.tsx';

export const KYC_STATUSES = ['pending', 'approved', 'rejected', 'draft'] as const;

/** Opções do filtro da fila: `awaiting_didit` é pendente que a Didit ainda não aprovou. */
export const KYC_VIEWS = [
  'pending',
  'awaiting_didit',
  'followup_submitted',
  'approved',
  'rejected',
  'draft',
] as const;

export function isKycView(value: unknown): value is AdminKycView {
  return KYC_VIEWS.some((view) => view === value);
}

const STATUS_META: Record<AdminKycStatus, { tone: PillTone; label: string }> = {
  pending: { tone: 'wait', label: 'Na fila' },
  approved: { tone: 'ok', label: 'Aprovado' },
  rejected: { tone: 'bad', label: 'Reprovado' },
  draft: { tone: 'neutral', label: 'Rascunho' },
};

export function isKycStatus(value: unknown): value is AdminKycStatus {
  return KYC_STATUSES.some((status) => status === value);
}

/** Status fora do enum mostra o valor cru em vez de sumir da tela. */
export function statusPill(status: string): { tone: PillTone; label: string } {
  return isKycStatus(status) ? STATUS_META[status] : { tone: 'neutral', label: status };
}

const FILE_LABELS: Record<AdminKycFileKind, string> = {
  id_front: 'Documento, frente',
  id_back: 'Documento, verso',
  selfie: 'Selfie com documento',
  company_doc: 'Documento da empresa',
  id_digital: 'Documento digital (CNH/RG)',
};

export function fileLabel(kind: string): string {
  return kind in FILE_LABELS ? FILE_LABELS[kind as AdminKycFileKind] : kind;
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * Quanto tempo a submissão está esperando. `null` = nunca foi enviada (rascunho), aí
 * não existe espera para medir.
 */
export function waitingMs(submittedAt: string | null, now: Date = new Date()): number | undefined {
  if (submittedAt === null) {
    return undefined;
  }
  const elapsed = now.getTime() - new Date(submittedAt).getTime();
  return Number.isFinite(elapsed) ? Math.max(0, elapsed) : undefined;
}

/** `3 dias` · `4 h` · `12 min` · `agora`. Sem casa decimal: é para bater o olho. */
export function formatWaiting(submittedAt: string | null, now: Date = new Date()): string {
  const elapsed = waitingMs(submittedAt, now);
  if (elapsed === undefined) {
    return '-';
  }
  if (elapsed >= DAY_MS) {
    const days = Math.floor(elapsed / DAY_MS);
    return days === 1 ? '1 dia' : `${days} dias`;
  }
  if (elapsed >= HOUR_MS) {
    return `${Math.floor(elapsed / HOUR_MS)} h`;
  }
  if (elapsed >= MINUTE_MS) {
    return `${Math.floor(elapsed / MINUTE_MS)} min`;
  }
  return 'agora';
}

/** Passou de um dia na fila: o número de espera fica dourado na tela. */
export function isOverdue(submittedAt: string | null, now: Date = new Date()): boolean {
  return (waitingMs(submittedAt, now) ?? 0) >= DAY_MS;
}

/** `19131243000197` → `19.131.243/0001-97`; CPF → `529.982.247-25`. */
export function formatDocument(document: string | undefined): string {
  if (document === undefined || document === '') {
    return '-';
  }
  const digits = document.replace(/\D/g, '');
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  }
  if (digits.length === 14) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
  }
  return document;
}

export function isNotFound(error: unknown): boolean {
  return isApiError(error) && (error.status === 404 || error.code === 'resource_not_found');
}

/**
 * `409 invalid_state` com `details.reason = 'partner_decides'`: o adquirente live do
 * seller tem `partnerKyc` (SELLER-06) e é ele quem decide, o admin não aprova, mas
 * continua podendo reprovar (`apps/api/src/modules/kyc/admin-routes.ts`).
 */
export function isPartnerDecides(error: unknown): boolean {
  return (
    isApiError(error) &&
    error.code === 'invalid_state' &&
    error.details?.reason === 'partner_decides'
  );
}

/**
 * Os documentos só vêm com step-up recente (`files[].view_url`, `spec/17` §4.3). Sem
 * ele a tela mostra o convite ao step-up, nunca um erro.
 */
export function documentsHidden(files: { view_url?: string }[]): boolean {
  return files.length > 0 && !files.some((file) => file.view_url !== undefined);
}

/** A linha mais antiga da fila é o foco de `/admin/kyc` (§8.2). */
export function oldestOf<T extends { submitted_at: string | null }>(rows: T[]): T | undefined {
  return rows.reduce<T | undefined>((oldest, row) => {
    if (row.submitted_at === null) {
      return oldest;
    }
    if (oldest?.submitted_at == null) {
      return row;
    }
    return row.submitted_at < oldest.submitted_at ? row : oldest;
  }, undefined);
}
